/* ============================================================
   SINGSPACE - SYSTÈME DE MAPPING UNIFIÉ DES CHAMPS
   Mapping bidirectionnel entre composants hardcodés et sections dynamiques
   ============================================================ */
import { ConditionOperator, FieldType } from '../types';
// ============================================================
// MAPPING COMPLET DES CHAMPS
// ============================================================
/**
 * Table de mapping complète de tous les champs du formulaire de réservation
 * Organisée par section pour faciliter la navigation
 */
export const FORM_FIELD_MAPPING = {
    // ============================================================
    // SECTION 1: INFORMATIONS PERSONNELLES (Toujours présente)
    // ============================================================
    personal_info: {
        key: 'personal_info',
        title: 'Informations personnelles',
        description: 'Coordonnées du demandeur',
        order: 1,
        isAlwaysPresent: true,
        fields: [
            {
                hardcodedKey: 'firstName',
                dynamicSectionKey: 'personal_info',
                dynamicFieldKey: 'firstName',
                label: 'Prénom',
                fieldType: FieldType.TEXT,
                isRequired: true,
                isConditional: false,
                bookingFieldMapping: 'customerName', // Combiné avec lastName
                hasDynamicSection: true,
                notes: 'Combiné avec lastName pour former customerName',
            },
            {
                hardcodedKey: 'lastName',
                dynamicSectionKey: 'personal_info',
                dynamicFieldKey: 'lastName',
                label: 'Nom',
                fieldType: FieldType.TEXT,
                isRequired: true,
                isConditional: false,
                bookingFieldMapping: 'customerName', // Combiné avec firstName
                hasDynamicSection: true,
                notes: 'Combiné avec firstName pour former customerName',
            },
            {
                hardcodedKey: 'email',
                dynamicSectionKey: 'personal_info',
                dynamicFieldKey: 'email',
                label: 'Email',
                fieldType: FieldType.EMAIL,
                isRequired: true,
                isConditional: false,
                bookingFieldMapping: 'customerEmail',
                hasDynamicSection: true,
            },
            {
                hardcodedKey: 'phone',
                dynamicSectionKey: 'personal_info',
                dynamicFieldKey: 'phone',
                label: 'Téléphone',
                fieldType: FieldType.PHONE,
                isRequired: true,
                isConditional: false,
                bookingFieldMapping: 'customerPhone',
                hasDynamicSection: true,
                notes: 'Format: +241 XX XX XX XX',
            },
        ],
    },
    // ============================================================
    // SECTION 2: INFORMATIONS ENTREPRISE (Conditionnelle)
    // ============================================================
    company_info: {
        key: 'company_info',
        title: 'Informations entreprise',
        description: "Coordonnées de l'entreprise",
        order: 2,
        isAlwaysPresent: false,
        condition: 'requireCompanyName === true',
        fields: [
            {
                hardcodedKey: 'companyName',
                dynamicSectionKey: 'company_info',
                dynamicFieldKey: 'companyName',
                label: 'Entreprise', // Personnalisable via config.companyFieldLabel
                fieldType: FieldType.TEXT,
                isRequired: true, // Si section visible
                isConditional: true,
                bookingFieldMapping: 'companyName',
                hasDynamicSection: true,
                notes: 'Label personnalisable via spaceType.companyFieldLabel',
            },
            {
                hardcodedKey: 'companyPhone',
                dynamicSectionKey: 'company_info',
                dynamicFieldKey: 'companyPhone',
                label: 'Téléphone entreprise', // Personnalisable via config.companyPhoneLabel
                fieldType: FieldType.PHONE,
                isRequired: true, // Si requireCompanyInfo === true
                isConditional: true,
                hasDynamicSection: true,
                notes: 'Visible si requireCompanyPhone === true',
            },
            {
                hardcodedKey: 'companyEmail',
                dynamicSectionKey: 'company_info',
                dynamicFieldKey: 'companyEmail',
                label: 'Email entreprise', // Personnalisable via config.companyEmailLabel
                fieldType: FieldType.EMAIL,
                isRequired: true, // Si requireCompanyInfo === true
                isConditional: true,
                hasDynamicSection: true,
                notes: 'Visible si requireCompanyEmail === true',
            },
            {
                hardcodedKey: 'companyWebsite',
                dynamicSectionKey: 'company_info',
                dynamicFieldKey: 'companyWebsite',
                label: 'Site web', // Personnalisable via config.companyWebsiteLabel
                fieldType: FieldType.TEXT,
                isRequired: false,
                isConditional: true,
                hasDynamicSection: true,
                notes: 'Visible si requireCompanyWebsite === true',
            },
        ],
    },
    // ============================================================
    // SECTION 3: TYPE D'ÉVÉNEMENT (Conditionnelle)
    // ============================================================
    event_type: {
        key: 'event_type',
        title: "Type d'événement",
        description: "Sélectionnez le type d'événement",
        order: 3,
        isAlwaysPresent: false,
        condition: 'eventTypesEnabled === true AND eventTypes.length > 0',
        fields: [
            {
                hardcodedKey: 'eventType',
                dynamicSectionKey: 'event_type',
                dynamicFieldKey: 'eventType',
                label: "Type d'événement",
                fieldType: FieldType.SELECT,
                isRequired: true,
                isConditional: false,
                hasDynamicSection: true,
                notes: 'Options dynamiques depuis config.eventTypes + option "Autre"',
            },
            {
                hardcodedKey: 'eventTypeOther',
                dynamicSectionKey: 'event_type',
                dynamicFieldKey: 'eventTypeOther',
                label: "Précisez le type d'événement",
                fieldType: FieldType.TEXTAREA,
                isRequired: true, // Si visible
                isConditional: true,
                dependsOnFieldKey: 'eventType',
                dependsOnOperator: ConditionOperator.EQUALS,
                dependsOnValue: 'other',
                validation: { maxLength: 50 },
                hasDynamicSection: true,
                notes: 'Visible uniquement si eventType === "other"',
            },
            {
                hardcodedKey: 'eventObjectives',
                dynamicSectionKey: 'event_type',
                dynamicFieldKey: 'eventObjectives',
                label: "Objectifs de l'événement", // Personnalisable via config.eventObjectivesLabel
                fieldType: FieldType.TEXTAREA,
                isRequired: true, // Si requireEventObjectives === true
                isConditional: true,
                hasDynamicSection: false, // ❌ CHAMP MANQUANT DANS SECTION DYNAMIQUE
                notes: 'CRITIQUE: Existe dans EventTypeSection mais PAS dans section dynamique générée par FormGeneratorService',
            },
        ],
    },
    // ============================================================
    // SECTION 4: RAISON DE LA RÉSERVATION (Conditionnelle)
    // ============================================================
    booking_reason: {
        key: 'booking_reason',
        title: 'Raison de la réservation',
        description: 'Décrivez la raison de votre réservation',
        order: 4,
        isAlwaysPresent: false,
        condition: 'requireBookingReason === true',
        fields: [
            {
                hardcodedKey: 'bookingReason',
                dynamicSectionKey: 'booking_reason',
                dynamicFieldKey: 'reason',
                label: 'Raison de la réservation', // Personnalisable via config.bookingReasonLabel
                fieldType: FieldType.TEXTAREA,
                isRequired: true,
                isConditional: false,
                validation: { maxLength: 2500 },
                hasDynamicSection: true,
                notes: '⚠️ DÉSYNCHRONISATION: Composant utilise "bookingReason" mais section dynamique génère "reason"',
            },
            {
                hardcodedKey: 'reason',
                dynamicSectionKey: 'booking_reason',
                dynamicFieldKey: 'reason',
                label: 'Raison de la réservation',
                fieldType: FieldType.TEXTAREA,
                isRequired: true,
                isConditional: false,
                validation: { maxLength: 2500 },
                hasDynamicSection: true,
                notes: 'Alias de bookingReason pour compatibilité',
            },
        ],
    },
    // ============================================================
    // SECTION 5: MISE EN PLACE (Conditionnelle - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    room_layout: {
        key: 'room_layout',
        title: 'Mise en place',
        description: 'Sélectionnez la configuration de la salle',
        order: 5,
        isAlwaysPresent: false,
        condition: 'roomLayouts.length > 0',
        fields: [
            {
                hardcodedKey: 'roomLayoutId',
                dynamicSectionKey: 'room_layout',
                dynamicFieldKey: 'roomLayoutId',
                label: 'Mise en place',
                fieldType: FieldType.SELECT,
                isRequired: true,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'CRITIQUE: Cause l\'erreur "Mise en place est requis" - Aucune section dynamique correspondante',
            },
            {
                hardcodedKey: 'roomLayoutOtherName',
                dynamicSectionKey: 'room_layout',
                dynamicFieldKey: 'roomLayoutOtherName',
                label: 'Nom de la mise en place personnalisée',
                fieldType: FieldType.TEXT,
                isRequired: true, // Si visible
                isConditional: true,
                dependsOnFieldKey: 'roomLayoutId',
                dependsOnOperator: ConditionOperator.CONTAINS,
                dependsOnValue: 'autre',
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Visible si le layout sélectionné contient "autre" (case-insensitive)',
            },
            {
                hardcodedKey: 'roomLayoutOther',
                dynamicSectionKey: 'room_layout',
                dynamicFieldKey: 'roomLayoutOther',
                label: 'Description de la mise en place',
                fieldType: FieldType.TEXTAREA,
                isRequired: false,
                isConditional: true,
                dependsOnFieldKey: 'roomLayoutId',
                dependsOnOperator: ConditionOperator.CONTAINS,
                dependsOnValue: 'autre',
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Optionnel - Description supplémentaire si "autre"',
            },
        ],
    },
    // ============================================================
    // SECTION 6: CAPACITÉ (Conditionnelle - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    capacity: {
        key: 'capacity',
        title: 'Capacité',
        description: 'Nombre de participants',
        order: 6,
        isAlwaysPresent: false,
        condition: 'capacityBrackets.length > 0',
        fields: [
            {
                hardcodedKey: 'participantCount',
                dynamicSectionKey: 'capacity',
                dynamicFieldKey: 'participantCount',
                label: 'Nombre de participants',
                fieldType: FieldType.NUMBER,
                isRequired: true,
                isConditional: false,
                bookingFieldMapping: 'participantCount',
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Utilisé pour le calcul de prix et la validation des documents',
            },
            {
                hardcodedKey: 'peopleCount',
                dynamicSectionKey: 'capacity',
                dynamicFieldKey: 'peopleCount',
                label: 'Nombre de personnes',
                fieldType: FieldType.NUMBER,
                isRequired: true,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Alias de participantCount',
            },
        ],
    },
    // ============================================================
    // SECTION 7: DATE ET DURÉE (Toujours présente - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    date_time: {
        key: 'date_time',
        title: 'Date et durée',
        description: 'Sélectionnez les dates de réservation',
        order: 7,
        isAlwaysPresent: true,
        fields: [
            {
                hardcodedKey: 'startDate',
                dynamicSectionKey: 'date_time',
                dynamicFieldKey: 'startDate',
                label: 'Date de début',
                fieldType: FieldType.DATE,
                isRequired: true,
                isConditional: false,
                bookingFieldMapping: 'startDate',
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Utilisé pour le calcul de prix et la disponibilité',
            },
            {
                hardcodedKey: 'endDate',
                dynamicSectionKey: 'date_time',
                dynamicFieldKey: 'endDate',
                label: 'Date de fin',
                fieldType: FieldType.DATE,
                isRequired: false,
                isConditional: true,
                bookingFieldMapping: 'endDate',
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Optionnel - Visible si allowMultiDay === true',
            },
        ],
    },
    // ============================================================
    // SECTION 8: ÉQUIPEMENTS (Conditionnelle - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    equipment: {
        key: 'equipment',
        title: 'Équipements',
        description: 'Sélectionnez les équipements nécessaires',
        order: 8,
        isAlwaysPresent: false,
        condition: 'equipments.length > 0',
        fields: [
            {
                hardcodedKey: 'equipmentIds',
                dynamicSectionKey: 'equipment',
                dynamicFieldKey: 'equipmentIds',
                label: 'Équipements',
                fieldType: FieldType.CHECKBOX_GROUP,
                isRequired: false,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: "Array d'IDs d'équipements sélectionnés",
            },
            {
                hardcodedKey: 'equipmentQuantities',
                dynamicSectionKey: 'equipment',
                dynamicFieldKey: 'equipmentQuantities',
                label: 'Quantités',
                fieldType: FieldType.NUMBER,
                isRequired: false,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Object { equipmentId: quantity } pour chaque équipement',
            },
        ],
    },
    // ============================================================
    // SECTION 9: RESTAURATION (Conditionnelle - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    catering: {
        key: 'catering',
        title: 'Restauration',
        description: 'Options de restauration',
        order: 9,
        isAlwaysPresent: false,
        condition: 'equipments.some(e => e.triggersRestaurant)',
        fields: [
            {
                hardcodedKey: 'wantsCoffeeBreak',
                dynamicSectionKey: 'catering',
                dynamicFieldKey: 'wantsCoffeeBreak',
                label: 'Pause café',
                fieldType: FieldType.CHECKBOX,
                isRequired: false,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Affecte le calcul de prix',
            },
        ],
    },
    // ============================================================
    // SECTION 10: DOCUMENTS REQUIS (Conditionnelle - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    documents: {
        key: 'documents',
        title: 'Documents requis',
        description: 'Téléchargez les documents justificatifs',
        order: 10,
        isAlwaysPresent: false,
        condition: 'requiredDocs.length > 0',
        fields: [
            {
                hardcodedKey: 'documents',
                dynamicSectionKey: 'documents',
                dynamicFieldKey: 'documents',
                label: 'Documents',
                fieldType: FieldType.FILE,
                isRequired: true, // Validation manuelle dans BookingFlowRenderer
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Validation manuelle complexe dans BookingFlowRenderer (lignes 130-170)',
            },
        ],
    },
    // ============================================================
    // SECTION 11: DÉCHARGE DE RESPONSABILITÉ (Toujours présente - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    disclaimer: {
        key: 'disclaimer',
        title: 'Décharge de responsabilité',
        description: 'Acceptation des conditions',
        order: 11,
        isAlwaysPresent: true,
        fields: [
            {
                hardcodedKey: 'acceptedDisclaimer',
                dynamicSectionKey: 'disclaimer',
                dynamicFieldKey: 'acceptedDisclaimer',
                label: "J'accepte la décharge de responsabilité",
                fieldType: FieldType.CHECKBOX,
                isRequired: true,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Validation manuelle dans BookingFlowRenderer (lignes 172-188)',
            },
        ],
    },
    // ============================================================
    // SECTION 12: DEVIS URGENT (Conditionnelle - PAS DE SECTION DYNAMIQUE)
    // ============================================================
    urgent_quote: {
        key: 'urgent_quote',
        title: 'Devis urgent',
        description: 'Option de traitement prioritaire',
        order: 12,
        isAlwaysPresent: false,
        condition: 'workflow === "QUOTE_REQUIRED"',
        fields: [
            {
                hardcodedKey: 'urgentQuote',
                dynamicSectionKey: 'urgent_quote',
                dynamicFieldKey: 'urgentQuote',
                label: 'Devis urgent',
                fieldType: FieldType.CHECKBOX,
                isRequired: false,
                isConditional: false,
                hasDynamicSection: false, // ❌ PAS DE SECTION DYNAMIQUE
                notes: 'Ajoute des frais supplémentaires si coché',
            },
        ],
    },
};
// ============================================================
// FONCTIONS UTILITAIRES
// ============================================================
/**
 * Récupère la clé de champ dynamique à partir d'une clé hardcodée
 * @param hardcodedKey - Clé du champ hardcodé (ex: 'companyName')
 * @returns Clé du champ dynamique (ex: 'companyName') ou undefined si non trouvé
 */
export function getFieldKey(hardcodedKey) {
    for (const section of Object.values(FORM_FIELD_MAPPING)) {
        const field = section.fields.find((f) => f.hardcodedKey === hardcodedKey);
        if (field) {
            return field.dynamicFieldKey;
        }
    }
    return undefined;
}
/**
 * Récupère la clé de section dynamique à partir d'une clé de champ hardcodée
 * @param hardcodedKey - Clé du champ hardcodé (ex: 'companyName')
 * @returns Clé de la section dynamique (ex: 'company_info') ou undefined si non trouvé
 */
export function getSectionKey(hardcodedKey) {
    for (const section of Object.values(FORM_FIELD_MAPPING)) {
        const field = section.fields.find((f) => f.hardcodedKey === hardcodedKey);
        if (field) {
            return field.dynamicSectionKey;
        }
    }
    return undefined;
}
/**
 * Récupère tous les champs hardcodés (avec et sans section dynamique)
 * @returns Array de tous les FieldMapping
 */
export function getAllHardcodedFields() {
    const allFields = [];
    for (const section of Object.values(FORM_FIELD_MAPPING)) {
        allFields.push(...section.fields);
    }
    return allFields;
}
/**
 * Récupère uniquement les champs hardcodés qui n'ont PAS de section dynamique
 * @returns Array des FieldMapping sans section dynamique
 */
export function getFieldsWithoutDynamicSection() {
    return getAllHardcodedFields().filter((field) => !field.hasDynamicSection);
}
/**
 * Récupère le mapping d'un champ spécifique
 * @param hardcodedKey - Clé du champ hardcodé
 * @returns FieldMapping ou undefined si non trouvé
 */
export function getFieldMapping(hardcodedKey) {
    for (const section of Object.values(FORM_FIELD_MAPPING)) {
        const field = section.fields.find((f) => f.hardcodedKey === hardcodedKey);
        if (field) {
            return field;
        }
    }
    return undefined;
}
/**
 * Transforme les données du formulaire en utilisant le mapping
 * Convertit les clés hardcodées en clés dynamiques
 * @param formData - Données du formulaire avec clés hardcodées
 * @returns Données transformées avec clés dynamiques
 */
export function mapFormDataToSections(formData) {
    const sectionData = {};
    for (const [hardcodedKey, value] of Object.entries(formData)) {
        const mapping = getFieldMapping(hardcodedKey);
        if (mapping) {
            // Initialiser la section si elle n'existe pas
            if (!sectionData[mapping.dynamicSectionKey]) {
                sectionData[mapping.dynamicSectionKey] = {};
            }
            // Ajouter le champ à la section
            sectionData[mapping.dynamicSectionKey][mapping.dynamicFieldKey] = value;
        }
    }
    return sectionData;
}
/**
 * Transforme les données du formulaire pour la persistance dans Booking.specificDetails
 * Capture TOUS les champs du formulaire (hardcodés et dynamiques)
 * @param formData - Données du formulaire
 * @returns Object JSON complet pour specificDetails
 */
export function mapFormDataToSpecificDetails(formData) {
    const specificDetails = {};
    const allFields = getAllHardcodedFields();
    for (const field of allFields) {
        const value = formData[field.hardcodedKey];
        if (value !== undefined && value !== null) {
            specificDetails[field.hardcodedKey] = value;
        }
    }
    return specificDetails;
}
/**
 * Vérifie si un champ existe dans le mapping
 * @param hardcodedKey - Clé du champ hardcodé
 * @returns true si le champ existe dans le mapping
 */
export function isFieldMapped(hardcodedKey) {
    return getFieldMapping(hardcodedKey) !== undefined;
}
/**
 * Récupère tous les champs critiques (ceux qui causent le bug)
 * @returns Array des FieldMapping critiques
 */
export function getCriticalFields() {
    const criticalKeys = ['roomLayoutId', 'eventObjectives', 'roomLayoutOtherName', 'companyName'];
    return getAllHardcodedFields().filter((field) => criticalKeys.includes(field.hardcodedKey));
}
/**
 * Récupère les statistiques du mapping
 * @returns Object avec les statistiques
 */
export function getMappingStats() {
    const allFields = getAllHardcodedFields();
    const fieldsWithDynamic = allFields.filter((f) => f.hasDynamicSection);
    const fieldsWithoutDynamic = allFields.filter((f) => !f.hasDynamicSection);
    const critical = getCriticalFields();
    return {
        totalFields: allFields.length,
        fieldsWithDynamicSection: fieldsWithDynamic.length,
        fieldsWithoutDynamicSection: fieldsWithoutDynamic.length,
        criticalFields: critical.length,
        sections: Object.keys(FORM_FIELD_MAPPING).length,
    };
}
